import { useMemo, useState } from "react";
import { CATEGORY } from "@/lib/constants";
import { FileText, Smartphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const hm = (m) => `${String(Math.floor(((m % 1440) + 1440) % 1440 / 60)).padStart(2, "0")}:${String(((m % 60) + 60) % 60).padStart(2, "0")}`;

export const StaffGantt = ({ units, eventId, shareToken }) => {
  const [showIdle, setShowIdle] = useState(false);
  const rows = showIdle ? units : units.filter((u) => u.segments.length);
  const [t0, t1] = useMemo(() => {
    const all = units.flatMap((u) => u.segments.flatMap((s) => [s.depart ?? s.start, s.end]).concat(u.segments.length && u.shift_from != null ? [u.shift_from, Math.min(u.shift_to, Math.max(...u.segments.map((s) => s.end)) + 60)] : []));
    if (!all.length) return [600, 720];
    return [Math.floor(Math.min(...all) / 60) * 60, Math.ceil(Math.max(...all) / 60) * 60];
  }, [units]);
  const span = Math.max(60, t1 - t0);
  const pct = (m) => `${((m - t0) / span) * 100}%`;
  const hours = [];
  for (let m = t0; m <= t1; m += span > 600 ? 120 : 60) hours.push(m);

  return (
    <div data-testid="staff-gantt-timeline-container">
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {Object.entries(CATEGORY).map(([k, c]) => (
          <span key={k} className="flex items-center gap-1.5 text-[0.6875rem] text-slate-500"><span className="w-3 h-3 rounded-sm" style={{ background: c.color }} />{c.label}</span>
        ))}
        <span className="flex items-center gap-1.5 text-[0.6875rem] text-slate-500"><span className="w-5 h-3 rounded-sm gantt-travel" />Desplazamiento</span>
        <span className="flex items-center gap-1.5 text-[0.6875rem] text-slate-500"><span className="w-5 h-3 rounded-sm border border-dashed border-sky-400/50 bg-sky-400/10" />Turno</span>
        <span className="flex items-center gap-1.5 text-[0.6875rem] text-slate-500"><span className="w-1 h-3 bg-yellow-300" />Relevo</span>
        <label className="ml-auto flex items-center gap-2 text-xs text-slate-500">Mostrar efectivos sin tarea <Switch checked={showIdle} onCheckedChange={setShowIdle} data-testid="gantt-show-idle-switch" /></label>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[820px] pr-6">
          <div className="flex">
            <div className="w-44 shrink-0" />
            <div className="relative flex-1 h-6 border-b border-slate-200">
              {hours.map((m) => (
                <span key={m} className="absolute -translate-x-1/2 font-mono text-[0.625rem] text-slate-500" style={{ left: pct(m) }}>{hm(m)}</span>
              ))}
            </div>
          </div>
          {rows.map((u) => (
            <div key={u.id} className="flex items-center border-b border-slate-200 hover:bg-white/[0.02]" data-testid={`gantt-row-${u.id}`}>
              <div className="w-44 shrink-0 py-2 pr-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-800">{u.nombre || u.id}</span>
                  {u.tip && !(u.nombre || "").startsWith("TIP") && <span className="font-mono text-[0.625rem] text-amber-700" data-testid={`gantt-tip-${u.id}`}>TIP {u.tip}</span>}
                  {eventId && u.segments.length > 0 && (
                    <a href={`/events/${eventId}/sheets?unit=${u.id}`} target="_blank" rel="noreferrer" title="Hoja de servicio" className="ml-auto text-slate-500 hover:text-sky-700" data-testid={`gantt-sheet-link-${u.id}`}>
                      <FileText className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {shareToken && u.segments.length > 0 && (
                    <a href={`/m/${shareToken}/${u.id}`} target="_blank" rel="noreferrer" title="Vista móvil del agente" className="text-slate-500 hover:text-sky-700" data-testid={`gantt-mobile-link-${u.id}`}>
                      <Smartphone className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
                <div className="text-[0.625rem] text-slate-500 truncate">{u.tipo || u.label.split(" · ")[0].replace(u.id, "")}{u.capacity > 1 ? ` · ${u.capacity} ag.` : ""}</div>
                {u.shift_hm && <div className="font-mono text-[0.5625rem] text-sky-600">TURNO {u.shift_hm}</div>}
              </div>
              <div className="relative flex-1 h-10">
                {hours.map((m) => <span key={m} className="absolute top-0 bottom-0 border-l border-slate-200" style={{ left: pct(m) }} />)}
                {u.shift_from != null && (
                  <div className="absolute top-1 bottom-1 rounded bg-sky-400/[0.07] border border-dashed border-sky-400/25"
                    style={{ left: pct(Math.max(t0, u.shift_from)), width: `${((Math.min(t1, u.shift_to) - Math.max(t0, u.shift_from)) / span) * 100}%` }}
                    title={`Turno ${u.shift_hm}`} data-testid={`gantt-shift-${u.id}`} />
                )}
                {u.segments.map((s, i) => (
                  <div key={i}>
                    {s.depart != null && s.depart < s.start && (
                      <div className="absolute top-3 h-4 gantt-travel rounded-l" style={{ left: pct(s.depart), width: `${((s.start - s.depart) / span) * 100}%` }}
                        title={`Desplazamiento desde ${s.from}: ${s.travel_min} min`} />
                    )}
                    <div className="absolute top-2 h-6 rounded-md px-1.5 flex items-center overflow-hidden text-[0.625rem] font-medium text-slate-900 shadow-lg hover:brightness-125 transition-[filter]"
                      style={{ left: pct(s.start), width: `${Math.max(0.6, ((s.end - s.start) / span) * 100)}%`, background: CATEGORY[s.category]?.color, borderLeft: s.handover ? "3px solid #fde047" : undefined }}
                      title={`${s.start_hm}–${s.end_hm} · ${s.name}${s.handover ? " · RELEVO / incorporación" : ""}${s.depart_hm ? ` · sale ${s.depart_hm} desde ${s.from}` : ""}`}>
                      <span className="truncate">{s.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!rows.length && <div className="text-sm text-slate-500 py-6 text-center">No hay asignaciones.</div>}
        </div>
      </div>
    </div>
  );
};
